import { ref, nextTick, type Ref } from "vue";
import type {
  ChainPaymentMethod,
  CreateDeliveryAreaMutation,
  CreateDeliveryAreaMutationVariables,
  UpdateDeliveryAreaMutation,
  UpdateDeliveryAreaMutationVariables,
  DeleteDeliveryAreaMutation,
  DeleteDeliveryAreaMutationVariables,
  DeliveryAreaCreateInput,
  DeliveryAreaUpdateInput,
  DeliveryAreaDeleteInput,
  DeliveryAreaSectionCountInput,
  DeliveryTimeListInput,
  DeliveryArea,
} from "@/generated/graphql";
import {
  CreateDeliveryAreaDocument,
  UpdateDeliveryAreaDocument,
  DeleteDeliveryAreaDocument,
} from "@/generated/graphql";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { countDeliverySection } from "@/common/graphql/deliverySection";
import { listDeliveryTime } from "@/common/graphql/deliveryTime";
import { getChainPaymentMethod } from "@/common/graphql/chain";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import type { DeliveryAreaInput, ChainSelectValue, ShopSelectValue } from "../types";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseAreaListAreaCrudParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  processFlag: Ref<number>;
  displayInputDialog: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  displayCompletedDialog: Ref<boolean>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  listArea: Ref<(DeliveryArea | null | undefined)[]>;
  paymentMethodItems: Ref<ChainPaymentMethod[]>;
  deliveryAreaInput: DeliveryAreaInput;
  onChangeShop: () => Promise<void>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useAreaListAreaCrud = ({
  errdialog,
  errMessage,
  overlay,
  processFlag,
  displayInputDialog,
  displayConfirmDialog,
  displayCompletedDialog,
  chainSelectValues,
  shopSelectValues,
  listArea,
  paymentMethodItems,
  deliveryAreaInput,
  onChangeShop,
}: UseAreaListAreaCrudParams) => {
  const createDeliveryAreaMutation= ref<CreateDeliveryAreaMutation | null>(null);

  // 初期化処理(新規)
  const initializeInput = async (): Promise<void> => {
    // 決済方法が未取得の場合は取得する
    if (paymentMethodItems.value.length === 0) {
      const chainCode = chainSelectValues.value.chain_code || localStorageGetChainCode();
      if (chainCode) {
        const paymentList = await getChainPaymentMethod(
          localStorageGetCustomerCode(),
          chainCode
        );
        paymentMethodItems.value = (paymentList ?? []).filter((p): p is ChainPaymentMethod => p !== null && p !== undefined);
      }
    }

    deliveryAreaInput.chain_code = chainSelectValues.value.chain_code;
    deliveryAreaInput.chain_name = chainSelectValues.value.name;
    deliveryAreaInput.shop_code = shopSelectValues.value.shop_code;
    deliveryAreaInput.shop_name = shopSelectValues.value.name;
    deliveryAreaInput.area_name = "";
    deliveryAreaInput.area_name_kana = "";
    deliveryAreaInput.payment_method = paymentMethodItems.value[0]?.payment_method ?? "";
    deliveryAreaInput.acceptable_end_time = "";
  };

  // 初期化処理(変更)
  const initializeUpdate = async (item: DeliveryArea): Promise<void> => {
    // 決済方法が未取得の場合は取得する
    if (paymentMethodItems.value.length === 0) {
      const chainCode = chainSelectValues.value.chain_code || localStorageGetChainCode();
      if (chainCode) {
        const paymentList = await getChainPaymentMethod(
          localStorageGetCustomerCode(),
          chainCode
        );
        paymentMethodItems.value = (paymentList ?? []).filter((p): p is ChainPaymentMethod => p !== null && p !== undefined);
      }
    }

    deliveryAreaInput.chain_code = chainSelectValues.value.chain_code;
    deliveryAreaInput.chain_name = chainSelectValues.value.name;
    deliveryAreaInput.shop_code = shopSelectValues.value.shop_code;
    deliveryAreaInput.shop_name = shopSelectValues.value.name;
    deliveryAreaInput.area_code = item.area_code;
    deliveryAreaInput.area_name = item.area_name;
    deliveryAreaInput.area_name_kana = item.area_name_kana;
    deliveryAreaInput.payment_method = item.payment_method ?? paymentMethodItems.value[0]?.payment_method ?? "";
    deliveryAreaInput.acceptable_end_time = item.acceptable_end_time;
  };

  // 初期化処理(削除)
  const initializeDelete = async (item: DeliveryArea): Promise<void> => {
    // 決済方法が未取得の場合は取得する
    if (paymentMethodItems.value.length === 0) {
      const chainCode = chainSelectValues.value.chain_code || localStorageGetChainCode();
      if (chainCode) {
        const paymentList = await getChainPaymentMethod(
          localStorageGetCustomerCode(),
          chainCode
        );
        paymentMethodItems.value = (paymentList ?? []).filter((p): p is ChainPaymentMethod => p !== null && p !== undefined);
      }
    }

    deliveryAreaInput.chain_code = chainSelectValues.value.chain_code;
    deliveryAreaInput.chain_name = chainSelectValues.value.name;
    deliveryAreaInput.shop_code = shopSelectValues.value.shop_code;
    deliveryAreaInput.shop_name = shopSelectValues.value.name;
    deliveryAreaInput.area_code = item.area_code;
    deliveryAreaInput.area_name = item.area_name;
    deliveryAreaInput.area_name_kana = item.area_name_kana;
    deliveryAreaInput.payment_method = item.payment_method ?? paymentMethodItems.value[0]?.payment_method ?? "";
  };

  // エリア削除可否チェック
  const checkAreaDelete = async (): Promise<boolean> => {
    try {
      // 配達区画チェック
      const countInput: DeliveryAreaSectionCountInput = {
        customer_code: deliveryAreaInput.customer_code,
        chain_code: deliveryAreaInput.chain_code,
        area_code: deliveryAreaInput.area_code,
        shop_code: deliveryAreaInput.shop_code,
      };
      const count = await countDeliverySection(countInput);
      if (count > 0) {
        errdialog.value = true;
        errMessage.value = "配達区画が登録されているため削除できません。配達区画を削除してください。";
        return false;
      }
      // 配達時間チェック
      const deliveryTimeInput: DeliveryTimeListInput = {
        customer_code: deliveryAreaInput.customer_code,
        chain_code: deliveryAreaInput.chain_code,
        area_code: [deliveryAreaInput.area_code],
        shop_code: deliveryAreaInput.shop_code,
        limit: 10000,
        offset: 0,
      };
      const listDelivery = await listDeliveryTime(deliveryTimeInput);
      if (listDelivery!.length > 0) {
        errdialog.value = true;
        errMessage.value = "配達時間が登録されているため削除できません。配達時間を削除してください。";
        return false;
      }
      return true;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
      return false;
    }
  };

  // エリア名重複チェック
  const onCheckAreaName = async (areaName: string, areaCode?: string): Promise<boolean> => {
    let findName: string | undefined;

    if (processFlag.value === 0) {
      //新規の場合エリア名のみでチェック
      findName = listArea.value.find(e => e!.area_name === areaName)?.area_name;
    } else {
      //新規以外の場合他のエリアコードに同一のエリア名が存在するかチェック
      findName = listArea.value.find(e => e!.area_code !== areaCode && e!.area_name === areaName)?.area_name;
    }
    if (findName) {
      errdialog.value = true;
      errMessage.value = `エリア名 :${areaName} は既に存在しています。`;
      return false;
    }
    return true;
  };

  // エリア登録・更新・削除処理
  const createArea = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();
    if (processFlag.value === 0) {
      const input: DeliveryAreaCreateInput = {
        area_name: deliveryAreaInput.area_name.trim(),
        area_name_kana: deliveryAreaInput.area_name_kana.trim(),
        chain_code: deliveryAreaInput.chain_code,
        customer_code: deliveryAreaInput.customer_code,
        payment_method: deliveryAreaInput.payment_method,
        acceptable_end_time: deliveryAreaInput.acceptable_end_time + ":00",
        shop_code: deliveryAreaInput.shop_code,
        user_id: deliveryAreaInput.user_id,
      };
      // 登録
      const result = await apolloClient.mutate<CreateDeliveryAreaMutation, CreateDeliveryAreaMutationVariables>({
        mutation: CreateDeliveryAreaDocument,
        variables: { input },
      });
      createDeliveryAreaMutation.value = result.data!;
    } else if (processFlag.value === 1) {
      const input: DeliveryAreaUpdateInput = {
        area_code: deliveryAreaInput.area_code,
        area_name: deliveryAreaInput.area_name.trim(),
        area_name_kana: deliveryAreaInput.area_name_kana.trim(),
        chain_code: deliveryAreaInput.chain_code,
        customer_code: deliveryAreaInput.customer_code,
        payment_method: deliveryAreaInput.payment_method,
        acceptable_end_time: deliveryAreaInput.acceptable_end_time + ":00",
        shop_code: deliveryAreaInput.shop_code,
        user_id: deliveryAreaInput.user_id,
      };
      const result = await apolloClient.mutate<UpdateDeliveryAreaMutation, UpdateDeliveryAreaMutationVariables>({
        mutation: UpdateDeliveryAreaDocument,
        variables: { input },
      });
      createDeliveryAreaMutation.value = result.data!;
    } else {
      const input: DeliveryAreaDeleteInput = {
        area_code: deliveryAreaInput.area_code,
        chain_code: deliveryAreaInput.chain_code,
        customer_code: deliveryAreaInput.customer_code,
        shop_code: deliveryAreaInput.shop_code,
        user_id: deliveryAreaInput.user_id,
      };
      const result = await apolloClient.mutate<DeleteDeliveryAreaMutation, DeleteDeliveryAreaMutationVariables>({
        mutation: DeleteDeliveryAreaDocument,
        variables: { input },
      });
      createDeliveryAreaMutation.value = result.data!;
    }
  };

  // 一覧表示 - 新規ボタン
  const onClickNewRegister = async (): Promise<void> => {
    await initializeInput();
    // エリア登録ダイアログ 表示
    displayInputDialog.value = true;
    // 新規フラグをONにする
    processFlag.value = 0;
  };

  // 一覧表示 - 編集ボタン
  const onClickEdit = async (item: DeliveryArea | null | undefined): Promise<void> => {
    if (!item) return;
    await initializeUpdate(item);
    // エリア登録ダイアログ 表示
    displayInputDialog.value = true;
    // 編集フラグをONにする
    processFlag.value = 1;
  };

  // 一覧表示 - 削除ボタン
  const onClickDelete = async (item: DeliveryArea | null | undefined): Promise<void> => {
    if (!item) return;
    await initializeDelete(item);
    const isDelete = await checkAreaDelete();
    if (!isDelete) return;
    displayConfirmDialog.value = true;
    processFlag.value = 2;
  };

  // 入力キャンセルボタン
  const onClickInputCancel = async (): Promise<void> => {
    // エリア登録ダイアログ 非表示
    displayInputDialog.value = false;
  };

  // 「登録手続きへ(確認)」ボタン - 確認ダイアログオープン
  const onClickOpenConfirmDialog = async (): Promise<void> => {
    //エリア名チェック
    if (!(await onCheckAreaName(deliveryAreaInput.area_name, deliveryAreaInput.area_code))) {
      return;
    }
    // エリア登録ダイアログ 非表示
    displayInputDialog.value = false;
    // エリア登録確認ダイアログ 表示
    displayConfirmDialog.value = true;
  };

  // 登録・削除実行
  const onClickConfirmEntry = async (): Promise<void> => {
    overlay.value = true;
    try {
      await createArea();
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
      return;
    }
    nextTick(async () => {
      overlay.value = false;
      displayConfirmDialog.value = false;
      if (processFlag.value < 2) {
        displayCompletedDialog.value = true;
      } else {
        await onChangeShop();
      }
    });
  };

  // 確認ダイアログキャンセル
  const onClickConfirmCancel = async (): Promise<void> => {
    if (processFlag.value < 2) {
      displayInputDialog.value = true;
    }
    displayConfirmDialog.value = false;
  };

  // 完了画面「戻る」
  const onClickCompletedGoStart = async (): Promise<void> => {
    // エリア登録完了ダイアログ 非表示
    displayCompletedDialog.value = false;
    nextTick(async () => {
      // 絞り込み検索 一覧再表示
      await onChangeShop();
    });
  };

  return {
    createDeliveryAreaMutation,
    onClickNewRegister,
    onClickEdit,
    onClickDelete,
    onClickInputCancel,
    onClickOpenConfirmDialog,
    onClickConfirmEntry,
    onClickConfirmCancel,
    onClickCompletedGoStart,
  };
};
